import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';

const GRID_SIZE = 50;
const DOT_RADIUS = 2;

type GridBackgroundProps = {
  zoomVar: {};
  dotMode?: boolean;
};

type GridRef = {
  setZoomProps: (v: any) => void;
};

const GridBackground = forwardRef<GridRef, GridBackgroundProps>(({ zoomVar, dotMode }, ref) => {
  if (!zoomVar) return null;

  const screenHeight = 3600;
  const screenWidth = 3000;
  const [zoomLevel, setZoomLevel] = useState(0.4);
  const adjustedGridSize = GRID_SIZE / zoomLevel;

  useImperativeHandle(ref, () => ({
    setZoomProps: (values: any) => {
      if (values.zoomLevel) {
        setZoomLevel(values.zoomLevel);
      }
    },
  }));

  const gridElements = useMemo(() => {
    // For web, we'll use CSS background patterns instead of individual elements
    return [];
  }, [dotMode, zoomLevel, adjustedGridSize, screenWidth, screenHeight]);

  const canvasOpacity = zoomLevel < 0.5 ? 0.4 : 0.2;

  // Create CSS grid pattern using background-image
  const gridStyle = useMemo(() => {
    if (dotMode) {
      // Dot pattern using CSS
      return {
        backgroundImage: `radial-gradient(circle at center, grey ${DOT_RADIUS * zoomLevel}px, transparent ${DOT_RADIUS * zoomLevel}px)`,
        backgroundSize: `${adjustedGridSize}px ${adjustedGridSize}px`,
        backgroundPosition: '0 0',
        opacity: canvasOpacity,
      };
    } else {
      // Line pattern using CSS
      return {
        backgroundImage: `
          linear-gradient(to right, grey 1px, transparent 1px),
          linear-gradient(to bottom, grey 1px, transparent 1px)
        `,
        backgroundSize: `${adjustedGridSize}px ${adjustedGridSize}px`,
        backgroundPosition: '0 0',
        opacity: canvasOpacity,
      };
    }
  }, [dotMode, adjustedGridSize, zoomLevel, canvasOpacity]);

  return (
    <View style={[styles.container, gridStyle]} pointerEvents="none" />
  );
});

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    position: 'absolute',
    left: -1100,
    top: -1200,
    width: 3000,
    height: 3600,
    zIndex: -1,
  },
});

export default GridBackground;
